import React, { useEffect, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { TailSpin } from "react-loader-spinner";
import { getApi } from "../../../utils/api";
import { MENU_USER } from "../../../config/Endpoints";
import { toast } from "react-toastify";
import { logoutUser } from "../../../utils/Storage";
import {
  dashboardRouteComponents,
  dbIndependentRoutes,
} from "../../../config/routeComponents";
import { DashboardLayout } from "./dashboard-layout.tsx";
import RouteWrapper from "./route-wrapper.tsx";
import { useUser } from "../../lib/rbac/useUser.ts";
import {
  isRestrictedForSQELabsMenu,
  isRestrictedForSQELabsPath,
  isSQELabsEmployeeType,
} from "../../utils/sqe-labs-navigation-access.ts";

function Home() {
  const user = useUser();
  const isSQELabsUser = isSQELabsEmployeeType(user?.empType);
  const [menuList, setMenuList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean | null>(null);

  const accessToken = Cookies.get("accessToken");
  const navigate = useNavigate();
  const [pageMeta, setPageMeta] = useState({
    title: "",
    description: "",
    isHome: false,
  });

  useEffect(() => {
    setIsLoading(true);

    getApi(MENU_USER, accessToken)
      .then((res) => {
        if (res.data.code === "200") {
          setMenuList(res.data.data);
        } else if (res.data.code === "500") {
          toast.error("Something went wrong please try again !!");
        } else {
          navigate("/");
        }
      })
      .catch((e) => {
        toast.error("Something went wrong please try again !!");
        logoutUser();
        console.log(e);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  return (
    <>
      {isLoading && (
        <div className="overlay">
          <div className="loader-container">
            <TailSpin
              height="100"
              width="100"
              radius="1"
              color="blue"
              ariaLabel="tail-spin-loading"
              visible={true}
            />
          </div>
        </div>
      )}

      <div className="wrapper">
        {!isLoading && isLoading !== null && (
          <>
            <Routes>
              <Route
                path="/dashboard"
                element={
                  <DashboardLayout menu={menuList} pageMeta={pageMeta} />
                }
              >
                {/* 🔹 Dynamic Routes from API */}
                {menuList?.map((item, index) => {
                  const Component = dashboardRouteComponents[item.achorValue];
                  const restrictedForSQELabs =
                    isSQELabsUser &&
                    (isRestrictedForSQELabsMenu(item.name) ||
                      isRestrictedForSQELabsPath(item.achorValue));

                  if (item.achorValue && restrictedForSQELabs) {
                    return (
                      <Route
                        key={`restricted-main-${index}`}
                        path={item.achorValue.slice(1)}
                        element={<Navigate to="/dashboard" replace />}
                      />
                    );
                  }

                  // 🏠 Home (/dashboard)
                  if (item.achorValue === "/" && Component) {
                    return (
                      <Route
                        key={`main-${index}`}
                        index
                        element={
                          <RouteWrapper
                            meta={{
                              title: item.name,
                              description: item.description,
                              isHome: true,
                            }}
                            setPageMeta={setPageMeta}
                          >
                            <Component
                              pageMeta={{
                                title: item.name,
                                description: item.description,
                              }}
                            />
                          </RouteWrapper>
                        }
                      />
                    );
                  }

                  return (
                    <React.Fragment key={`frag-${index}`}>
                      {/* 🔹 Main Menu Route */}
                      {item.achorValue && Component && (
                        <Route
                          path={item.achorValue.slice(1)}
                          element={
                            <RouteWrapper
                              meta={{
                                title: item.name,
                                description: item.description,
                                isHome: false,
                              }}
                              setPageMeta={setPageMeta}
                            >
                              <Component
                                pageMeta={{
                                  title: item.name,
                                  description: item.description,
                                }}
                              />
                            </RouteWrapper>
                          }
                        />
                      )}

                      {/* 🔹 Sub Menu Routes */}
                      {item.subMenu?.map((subItem: any, i: number) => {
                        const SubComponent =
                          dashboardRouteComponents[subItem.achorValue];
                        const restrictedSubRoute =
                          isSQELabsUser &&
                          (isRestrictedForSQELabsMenu(subItem.name) ||
                            isRestrictedForSQELabsPath(subItem.achorValue));

                        return (
                          restrictedSubRoute ? (
                            <Route
                              key={`restricted-sub-${i}`}
                              path={subItem.achorValue.slice(1)}
                              element={<Navigate to="/dashboard" replace />}
                            />
                          ) : SubComponent && (
                            <Route
                              key={`sub-${i}`}
                              path={subItem.achorValue.slice(1)}
                              element={
                                <RouteWrapper
                                  meta={{
                                    title: subItem.name,
                                    description: subItem.description,
                                    isHome: false,
                                  }}
                                  setPageMeta={setPageMeta}
                                >
                                  <SubComponent
                                    pageMeta={{
                                      title: subItem.name,
                                      description: subItem.description,
                                    }}
                                  />
                                </RouteWrapper>
                              }
                            />
                          )
                        );
                      })}
                    </React.Fragment>
                  );
                })}

                {/* 🔹 Independent Routes */}
                {dbIndependentRoutes.map((item, index) => {
                  const Component = dashboardRouteComponents[item.path];
                  if (
                    isSQELabsUser &&
                    (isRestrictedForSQELabsMenu(item.title) ||
                      isRestrictedForSQELabsPath(item.path))
                  ) {
                    return (
                      <Route
                        key={`restricted-ind-${index}`}
                        path={item.path.slice(1)}
                        element={<Navigate to="/dashboard" replace />}
                      />
                    );
                  }

                  return (
                    <Route
                      key={`ind-${index}`}
                      path={item.path.slice(1)}
                      element={
                        <RouteWrapper
                          meta={{
                            title: item.title,
                            description: item.description,
                            isHome: false,
                          }}
                          setPageMeta={setPageMeta}
                        >
                          <Component
                            pageMeta={{
                              title: item.title,
                              description: item.description,
                            }}
                          />
                        </RouteWrapper>
                      }
                    />
                  );
                })}
              </Route>
            </Routes>{" "}
          </>
        )}
      </div>
    </>
  );
}

export default Home;
